// N-1 comparison: the previous report's section (the one the agent reads) next
// to the current text, with a word-level diff. When the two texts are not in
// the same language (an English N-1 report, a French draft), a diff would mark
// everything as changed: the texts are shown side by side without one.
import { api } from "../api/client";
import { useResource } from "../api/resource";
import { useDossier } from "../dossier";
import { w } from "../i18n";
import { ReviewBadge } from "./drafting";
import { EmptyState, ErrorState, Notice, SkeletonBlock } from "./ui";

type Piece = { kind: "same" | "added" | "removed"; text: string };
const MAX_TOKENS = 3_000;
const FR = new Set(["le", "la", "les", "des", "du", "de", "et", "est", "sont", "une", "un", "pour", "dans", "par", "au", "aux", "sur", "que", "qui"]);
const EN = new Set(["the", "and", "of", "is", "are", "for", "in", "by", "to", "on", "that", "which", "with", "as", "an", "a"]);

/** The readable text of a draft: no citation markers, headings or bullets marks. */
export function plain(text: string): string {
  return text.replace(/\[\d+\]/g, "").replace(/\*\*/g, "").replace(/^#{1,6}\s*/gm, "").replace(/^[-*•]\s+/gm, "").trim();
}

export function language(text: string): "fr" | "en" | null {
  const words = text.toLowerCase().match(/[a-zàâçéèêëîïôûùüÿœ]+/g) || [];
  const fr = words.filter(word => FR.has(word)).length, en = words.filter(word => EN.has(word)).length;
  if (fr + en < 8) return null;
  return fr > en * 1.5 ? "fr" : en > fr * 1.5 ? "en" : null;
}

/** Word-level diff (longest common subsequence); null when the texts are too long to compare. */
export function diffWords(before: string, after: string): Piece[] | null {
  const a = before.split(/(\s+)/).filter(Boolean), b = after.split(/(\s+)/).filter(Boolean);
  if (a.length > MAX_TOKENS || b.length > MAX_TOKENS) return null;
  const n = a.length, m = b.length;
  const table = new Uint16Array((n + 1) * (m + 1));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      table[i * (m + 1) + j] = a[i] === b[j] ? table[(i + 1) * (m + 1) + j + 1] + 1
        : Math.max(table[(i + 1) * (m + 1) + j], table[i * (m + 1) + j + 1]);
    }
  }
  const pieces: Piece[] = [];
  const push = (kind: Piece["kind"], text: string) => {
    const last = pieces[pieces.length - 1];
    if (last && last.kind === kind) last.text += text; else pieces.push({ kind, text });
  };
  let i = 0, j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) { push("same", a[i]); i++; j++; }
    else if (table[(i + 1) * (m + 1) + j] >= table[i * (m + 1) + j + 1]) { push("removed", a[i]); i++; }
    else { push("added", b[j]); j++; }
  }
  while (i < n) push("removed", a[i++]);
  while (j < m) push("added", b[j++]);
  return pieces;
}

function changedShare(pieces: Piece[]): number {
  const words = (kind: Piece["kind"]) => pieces.filter(p => p.kind === kind).reduce((count, p) => count + (p.text.match(/\S+/g) || []).length, 0);
  const same = words("same"), changed = words("added") + words("removed");
  return same + changed ? Math.round(changed / (same + changed) * 100) : 0;
}

export function ComparisonTab({ code }: { code: string }) {
  const { dossier } = useDossier();
  const data = useResource(`comparison:${dossier.id}:${code}`, () => api.sectionComparison(dossier.id, code));
  if (data.loading) return <SkeletonBlock lines={8} />;
  if (data.error) return <ErrorState title={w("common.genericError")} error={data.error} onRetry={data.reload} />;
  const { previous, current } = data.data!;
  if (!previous) return <div className="ws-card"><EmptyState title={w("compare.noPrevious")} body={w("compare.noPreviousBody")} /></div>;
  const before = previous.text, after = current ? plain(current.text) : "";
  const lb = language(before), la = language(after);
  const sameLanguage = !current || !lb || !la || lb === la;
  const pieces = current && sameLanguage ? diffWords(before, after) : null;

  return <div className="ws-stack">
    <div className="ws-card ws-card-pad ws-row" style={{ justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
      <span className="ws-small">{w("compare.source", { name: previous.document.name,
        sections: previous.sections.map(s => `${s.code} ${s.title}`).join(", ") })}</span>
      {current && <span className="ws-row">{pieces && <span className="ws-tag">{w("compare.changed", { share: changedShare(pieces) })}</span>}
        <ReviewBadge status={current.status} /></span>}
    </div>
    {!current && <Notice tone="neutral">{w("compare.noCurrent")}</Notice>}
    {current && !sameLanguage && <Notice tone="neutral">{w("compare.languages", { before: w(`lang.${lb}`), after: w(`lang.${la}`) })}</Notice>}
    {current && sameLanguage && !pieces && <Notice tone="neutral">{w("compare.tooLong")}</Notice>}
    {pieces
      ? <section className="ws-card"><div className="ws-card-head"><h2>{w("compare.diffTitle")}</h2>
          <span className="ws-small ws-muted"><del className="ws-diff-del">{w("compare.removed")}</del> · <ins className="ws-diff-ins">{w("compare.added")}</ins></span></div>
          <div className="ws-card-pad ws-diff-text">{pieces.map((piece, index) => piece.kind === "same"
            ? <span key={index}>{piece.text}</span>
            : piece.kind === "added" ? <ins key={index} className="ws-diff-ins">{piece.text}</ins>
            : <del key={index} className="ws-diff-del">{piece.text}</del>)}</div></section>
      : <div className="ws-grid-2" style={{ alignItems: "start" }}>
          <section className="ws-card"><div className="ws-card-head"><h2>{w("compare.previousTitle")}</h2></div>
            <div className="ws-card-pad ws-diff-text">{before}</div></section>
          <section className="ws-card"><div className="ws-card-head"><h2>{w("compare.currentTitle")}</h2></div>
            <div className="ws-card-pad ws-diff-text">{after || w("compare.noCurrentShort")}</div></section>
        </div>}
  </div>;
}
